/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { chartTooltip } from "@/components/common/ChartTooltip";
import { Card } from "@/components/ui/card";
import { PacketLossResult } from "@/types/types";
import { formatters } from "@/utils/timeSettings";

interface MonitorPerformanceChartProps {
  historyList: PacketLossResult[];
  selectedMonitorId: number;
  /** How many recent tests to chart; controlled by the parent so it can load
   *  more history pages to satisfy a wider window. Defaults to 30. */
  points?: number;
  onPointsChange?: (points: number) => void;
}

const POINT_OPTIONS = [30, 60, 120] as const;

/** p-th percentile of a numeric array (linear interpolation). */
function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

export const MonitorPerformanceChart: React.FC<
  MonitorPerformanceChartProps
> = ({ historyList, selectedMonitorId, points = 30, onPointsChange }) => {
  // Prepare chart data - use useMemo to ensure it updates when historyList changes
  const chartData = useMemo(() => {
    // historyList is in descending order (newest first); take the window and
    // reverse to show oldest -> newest.
    const data = historyList
      .slice(0, points)
      .reverse()
      .map((result) => {
        const date = new Date(result.createdAt);

        // Use timezone-aware formatting for all data points
        const timeLabel = formatters.chartTick(date, "1d");

        return {
          time: timeLabel,
          packetLoss: result.packetLoss,
          avgRtt: result.avgRtt,
          minRtt: result.minRtt,
          maxRtt: result.maxRtt,
          jitter: result.stdDevRtt,
        };
      });

    return data;
  }, [historyList, points]);

  // RTT statistics: axis scaling plus latency percentiles and average jitter.
  const rttStats = useMemo(() => {
    if (chartData.length === 0) return null;

    const allRttValues = chartData.flatMap(d => [d.avgRtt, d.minRtt, d.maxRtt]).filter(v => v > 0);
    if (allRttValues.length === 0) return null;

    const min = Math.min(...allRttValues);
    const max = Math.max(...allRttValues);
    const avgSeries = chartData.map(d => d.avgRtt).filter(v => v > 0);
    const avg = avgSeries.reduce((s, v) => s + v, 0) / (avgSeries.length || 1);
    const jitterSeries = chartData.map(d => d.jitter).filter(v => v > 0);
    const avgJitter = jitterSeries.length
      ? jitterSeries.reduce((s, v) => s + v, 0) / jitterSeries.length
      : 0;

    // Calculate a good range around the data
    const range = max - min;
    const padding = Math.max(range * 0.1, 5); // 10% padding or 5ms minimum

    return {
      min: Math.max(0, min - padding),
      max: max + padding,
      avg,
      p95: percentile(avgSeries, 95),
      avgJitter,
    };
  }, [chartData]);

  if (chartData.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="mb-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="text-gray-700 dark:text-gray-300 font-medium">
            Latency &amp; jitter
          </h3>
          {onPointsChange && (
            <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-gray-100/60 p-0.5 dark:border-gray-700 dark:bg-gray-800/50">
              {POINT_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onPointsChange(opt)}
                  className={
                    "rounded px-2 py-0.5 text-[11px] tabular-nums transition-colors " +
                    (points === opt
                      ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white"
                      : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200")
                  }
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-y-2">
          <p className="text-gray-600 dark:text-gray-400 text-xs">
            Last {chartData.length} tests
            {rttStats && (
              <>
                <span className="ml-2 text-blue-600 dark:text-blue-400">
                  avg {rttStats.avg.toFixed(1)}ms
                </span>
                <span className="ml-2 text-indigo-600 dark:text-indigo-400">
                  p95 {rttStats.p95.toFixed(1)}ms
                </span>
                <span className="ml-2 text-purple-600 dark:text-purple-400">
                  jitter {rttStats.avgJitter.toFixed(1)}ms
                </span>
              </>
            )}
          </p>
          <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-1">
              <div className="w-3 h-0.5 bg-red-500"></div>
              <span>Loss</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-0.5 bg-blue-500"></div>
              <span>Avg</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-0.5 bg-emerald-500 border-dashed"></div>
              <span>Min</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-0.5 bg-yellow-500 border-dashed"></div>
              <span>Max</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-0.5 bg-purple-500"></div>
              <span>Jitter</span>
            </div>
          </div>
        </div>
      </div>

      <Card className="h-80 p-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            key={`chart-${selectedMonitorId}-${historyList[0]?.id || 0}`}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(128, 128, 128, 0.15)"
              strokeWidth={0.5}
            />
            <XAxis
              dataKey="time"
              stroke="rgb(156, 163, 175)"
              fontSize={11}
              axisLine={false}
              tickLine={false}
              dy={10}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              stroke="rgb(156, 163, 175)"
              fontSize={11}
              axisLine={false}
              tickLine={false}
              scale="linear"
              domain={rttStats ? [Math.floor(rttStats.min), Math.ceil(rttStats.max)] : ['dataMin', 'dataMax']}
              allowDataOverflow={false}
              label={{
                value: "RTT (ms)",
                angle: -90,
                position: "insideLeft",
                style: {
                  fill: "rgb(156, 163, 175)",
                  textAnchor: "middle",
                },
              }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="rgb(217, 138, 127)"
              fontSize={11}
              axisLine={false}
              tickLine={false}
              scale="linear"
              domain={[0, 100]}
              label={{
                value: "Packet Loss (%)",
                angle: 90,
                position: "insideRight",
                style: {
                  fill: "rgb(217, 138, 127)",
                  textAnchor: "middle",
                },
              }}
            />
            <Tooltip
              {...chartTooltip({
                formatter: (value) => (typeof value === "number" ? value.toFixed(1) : value),
              })}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="packetLoss"
              stroke="rgb(217, 138, 127)"
              strokeWidth={2.5}
              name="Packet Loss %"
              dot={{
                fill: "rgb(217, 138, 127)",
                strokeWidth: 0,
                r: 3,
              }}
              activeDot={{
                r: 5,
                stroke: "rgb(217, 138, 127)",
                strokeWidth: 2,
              }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="avgRtt"
              stroke="rgb(134, 185, 160)"
              strokeWidth={2.5}
              name="Avg RTT"
              dot={{
                fill: "rgb(134, 185, 160)",
                strokeWidth: 0,
                r: 3,
              }}
              activeDot={{
                r: 5,
                stroke: "rgb(134, 185, 160)",
                strokeWidth: 2,
              }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="minRtt"
              stroke="rgb(154, 163, 156)"
              strokeWidth={1.5}
              strokeDasharray="4 4"
              name="Min RTT"
              dot={false}
              activeDot={{
                r: 4,
                stroke: "rgb(154, 163, 156)",
                strokeWidth: 2,
              }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="maxRtt"
              stroke="rgb(217, 178, 106)"
              strokeWidth={1.5}
              strokeDasharray="4 4"
              name="Max RTT"
              dot={false}
              activeDot={{
                r: 4,
                stroke: "rgb(217, 178, 106)",
                strokeWidth: 2,
              }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="jitter"
              stroke="rgb(168, 130, 230)"
              strokeWidth={1.5}
              name="Jitter"
              dot={false}
              activeDot={{
                r: 4,
                stroke: "rgb(168, 130, 230)",
                strokeWidth: 2,
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};
